import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { WorkspaceContext } from './workspace-context';
import type { WorkspaceContextValue } from './workspace-context';
import { useAuth } from './auth-context';
import { isActiveLocation, resolveLocationSelection } from './workspace-selection';
import type { Location, Membership, Role } from '@adop/shared';

const RESTAURANT_KEY = 'adop.platform.restaurant';
const LOCATION_KEY = 'adop.platform.location';

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode); selection just won't persist.
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { memberships, refresh } = useAuth();
  const [restaurantId, setRestaurantIdState] = useState<string | null>(() => readStored(RESTAURANT_KEY));
  const [locationId, setLocationIdState] = useState<string | null>(() => readStored(LOCATION_KEY));

  const restaurantOptions = useMemo(
    () => memberships.map((entry) => entry.restaurant),
    [memberships],
  );

  const restaurantSelection = useMemo(
    () => resolveLocationSelection(restaurantOptions, restaurantId),
    [restaurantOptions, restaurantId],
  );
  const restaurant = restaurantSelection.location;

  const visibleLocations = useMemo<Location[]>(() => {
    if (!restaurant) return [];
    const summary = memberships.find((entry) => entry.restaurant.id === restaurant.id);
    return summary?.locations ?? [];
  }, [memberships, restaurant]);

  // Archived locations stay manageable on the Locations page, but are never
  // offered as the active workspace in the header switcher.
  const selectableLocations = useMemo(
    () => visibleLocations.filter(isActiveLocation),
    [visibleLocations],
  );

  const locationSelection = useMemo(
    () => resolveLocationSelection(selectableLocations, locationId),
    [selectableLocations, locationId],
  );
  const location = locationSelection.location;

  const membership = useMemo<Membership | null>(() => {
    if (!restaurant) return null;
    const summary = memberships.find((entry) => entry.restaurant.id === restaurant.id);
    return summary?.membership ?? null;
  }, [memberships, restaurant]);

  // Persist a first-time selection. A stored id that is absent from the list is
  // treated as stale and deliberately left in storage so a refresh can restore it.
  useEffect(() => {
    if (restaurantSelection.persist && restaurantSelection.persist !== restaurantId) {
      setRestaurantIdState(restaurantSelection.persist);
      writeStored(RESTAURANT_KEY, restaurantSelection.persist);
    }
  }, [restaurantSelection.persist, restaurantId]);

  useEffect(() => {
    if (locationSelection.persist && locationSelection.persist !== locationId) {
      setLocationIdState(locationSelection.persist);
      writeStored(LOCATION_KEY, locationSelection.persist);
    }
  }, [locationSelection.persist, locationId]);

  // The cached `/me` response can lag behind the database (e.g. a new location
  // was added elsewhere, or the stored id was removed). Refresh it once rather
  // than clobbering the user's stored selection with the fallback.
  useEffect(() => {
    if (memberships.length === 0) return;
    if (restaurantSelection.missing || locationSelection.missing) {
      void refresh();
    }
  }, [memberships.length, restaurantSelection.missing, locationSelection.missing, refresh]);

  const setRestaurantId = useCallback((nextId: string) => {
    setRestaurantIdState(nextId);
    writeStored(RESTAURANT_KEY, nextId);
    // Reset the location so it snaps to the new restaurant's first location.
    setLocationIdState(null);
    try {
      window.localStorage.removeItem(LOCATION_KEY);
    } catch {
      // ignore
    }
  }, []);

  const setLocationId = useCallback((nextId: string) => {
    setLocationIdState(nextId);
    writeStored(LOCATION_KEY, nextId);
  }, []);

  const role = (membership?.role as Role | undefined) ?? null;

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      restaurants: memberships,
      restaurant,
      location,
      membership,
      role,
      visibleLocations,
      canManage: role === 'owner' || role === 'manager',
      canOwn: role === 'owner',
      setRestaurantId,
      setLocationId,
    }),
    [memberships, restaurant, location, membership, role, visibleLocations, setRestaurantId, setLocationId],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
